import { create } from 'zustand'
import { combine, devtools, persist } from 'zustand/middleware'
import {
  INBOX_CATEGORY_IDS,
  type InboxCategoryId,
} from '#src/hooks/inbox/inboxCategories.ts'

const validCategoryIds = new Set<string>(INBOX_CATEGORY_IDS)

export type InboxReadFilter = 'all' | 'unread' | 'read'

export const READ_FILTER_OPTIONS: { value: InboxReadFilter; label: string }[] =
  [
    { value: 'all', label: 'All' },
    { value: 'unread', label: 'Unread' },
    { value: 'read', label: 'Read' },
  ]

export const useInboxCategoryStore = create(
  devtools(
    persist(
      combine(
        {
          selectedCategory: 'all' as InboxCategoryId,
          readFilter: 'all' as InboxReadFilter,
        },
        (set) => ({
          setSelectedCategory: (category: InboxCategoryId) => {
            set({ selectedCategory: category })
          },
          setReadFilter: (filter: InboxReadFilter) => {
            set({ readFilter: filter })
          },
        })
      ),
      {
        name: 'inbox-category-store',
        /*
         * Version bump to handle migration from old category IDs.
         * v1: tasks, mentions, comments, appointments -> documents, scheduling, discussions, to-do
         * v2: to-do -> other-tasks
         * v3: added stat category
         * v4: added overdue category
         * v5: added readFilter ('all' | 'unread' | 'read')
         */
        version: 5,
        migrate: (persisted) => {
          const state = persisted as {
            selectedCategory?: string
            readFilter?: string
          }
          const selectedCategory =
            !state.selectedCategory ||
            !validCategoryIds.has(state.selectedCategory) ||
            state.selectedCategory === 'to-do'
              ? ('all' as InboxCategoryId)
              : (state.selectedCategory as InboxCategoryId)
          const readFilter: InboxReadFilter =
            state.readFilter === 'unread' || state.readFilter === 'read'
              ? state.readFilter
              : 'all'
          return { selectedCategory, readFilter }
        },
      }
    )
  )
)
